import React, { useCallback, useState, useRef } from 'react';
import { Box } from '@mui/material';
import palette from '@ui/palette';
import { makeStyles } from 'tss-react/mui';
import { isAcceptedFileType, isSizeLimitExceeded } from '../utils';
import EmptyZone from './EmptyZone';
import FilledZone from './FilledZone';

export type DropZoneProps = {
  accept: string;
  onChange: (file: File | null, errorType?: 'FILE_TYPE' | 'SIZE_LIMIT') => void;
  sizeLimit: string;
  disabled?: boolean;
};

const DropZone = ({ accept, onChange, sizeLimit, disabled }: DropZoneProps) => {
  const { classes, cx } = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;
      if (!file) {
        return;
      }

      if (!isAcceptedFileType(file, accept)) {
        // File type not accepted, clear the input and notify parent
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        onChange(null, 'FILE_TYPE');
        return;
      }

      if (isSizeLimitExceeded(file, sizeLimit)) {
        onChange(null, 'SIZE_LIMIT');
        return;
      }
      setCurrentFile(file);
      onChange(file);
    },
    [onChange, sizeLimit, accept]
  );

  const handleDropzoneClick = useCallback((event?: React.SyntheticEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isDragOver) {
        setIsDragOver(true);
      }
    },
    [isDragOver, setIsDragOver]
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
    },
    []
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      const { files } = event.dataTransfer;
      if (!files || files.length === 0) {
        return;
      }
      const [file] = files;
      const isAcceptedType = isAcceptedFileType(file, accept);
      if (!isAcceptedType) {
        // File type not accepted, clear the input and notify parent
        onChange(null, 'FILE_TYPE');
        return;
      }
      if (isSizeLimitExceeded(file, sizeLimit)) {
        // always clear file if limit exceeded
        onChange(null, 'SIZE_LIMIT');
        return;
      }
      setCurrentFile(file);
      onChange(file);
    },
    [onChange, sizeLimit, accept]
  );

  const handleRemoveFile = useCallback(() => {
    setIsDragOver(false);
    setCurrentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange(null);
  }, [onChange]);

  return (
    <>
      {!currentFile ? (
        <Box
          textAlign="center"
          className={cx(
            classes.dropzone,
            isDragOver && !disabled ? classes.dropzoneDragOver : '',
            disabled ? classes.dropzoneDisabled : ''
          )}
          onClick={disabled ? undefined : handleDropzoneClick}
          onDragEnter={handleDragEnter}
          onDragLeave={disabled ? undefined : handleDragLeave}
          onDragOver={disabled ? undefined : handleDragOver}
          onDrop={disabled ? undefined : handleDrop}
        >
          <EmptyZone accept={accept} onChooseFile={handleDropzoneClick} />
        </Box>
      ) : (
        <Box
          className={cx(
            classes.dropzone,
            classes.dropzoneFilled,
            disabled ? classes.dropzoneDisabled : ''
          )}
        >
          <FilledZone
            fileName={currentFile.name}
            fileSize={currentFile.size}
            disabled={!!disabled}
            handleRemoveFile={handleRemoveFile}
          />
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        className={classes.hiddenInput}
        aria-label="File upload input"
      />
    </>
  );
};

const useStyles = makeStyles()(() => ({
  dropzone: {
    border: `1px dashed ${palette.gray_500}`,
    borderRadius: 4,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    height: 274,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    backgroundColor: palette.gray_1000,
    '&:hover': {
      backgroundColor: palette.gray_900,
    },
  },
  dropzoneFilled: {
    backgroundColor: palette.gray_900,
  },
  dropzoneDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
    backgroundColor: palette.gray_800,
    '&:hover': {
      backgroundColor: palette.gray_800,
    },
  },
  dropzoneDragOver: {
    border: `2px dashed ${palette.mint_300}`,
    opacity: 0.8,
  },
  hiddenInput: {
    display: 'none',
  },
}));

export default DropZone;
